// Live-meeting reminders as browser notifications (Web Push): this browser subscribes with the platform's public
// key and the platform sends the reminders (see PushService / MeetingReminderService on the backend).
import { apiFetch } from "./Api";

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** iPhone / iPad: notifications only work for a site added to the home screen. */
export function needsHomeScreen() {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return ios && !standalone;
}

function keyBytes(base64url) {
  const pad = "=".repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function registration() {
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  return reg;
}

/** "on", "off", "blocked" or "unsupported". When on, the subscription is re-sent so it belongs to whoever is
 *  signed in now (two students sharing a browser). */
export async function pushState() {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  const reg = await navigator.serviceWorker.getRegistration("/");
  const sub = reg && (await reg.pushManager.getSubscription());
  if (!sub) return "off";
  apiFetch("/push/subscriptions", { method: "POST", auth: true, body: sub.toJSON() }).catch(() => {});
  return "on";
}

export async function enablePush() {
  if (!pushSupported()) throw new Error("This browser can't show notifications");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error(permission === "denied" ? "blocked" : "Notifications were not allowed");
  const reg = await registration();
  const { publicKey } = await apiFetch("/push/key", { auth: true });
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  await apiFetch("/push/subscriptions", { method: "POST", auth: true, body: sub.toJSON() });
  return "on";
}

export async function disablePush() {
  const reg = await navigator.serviceWorker.getRegistration("/");
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    await apiFetch("/push/subscriptions", { method: "DELETE", auth: true, body: { endpoint: sub.endpoint } }).catch(() => {});
    await sub.unsubscribe();
  }
  return "off";
}
